import { useMemo } from 'react';

import type { ReportResponseDto } from '@/api/WhisApi/generated';
import { FoldableSection } from '@/components/FoldableSection/FoldableSection';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { getStep2DetailFormConfig } from '@/screens/ReportCreation/forms/step2.config';
import type { FormSection } from '@/screens/ReportCreation/reportCreation.types';
import type { DetailFieldLabelOverrides } from '../reportDetail.types';
import {
  getReportFieldValue,
  hasMissingRequiredFields,
} from '../reportDetail.utils';
import { ReportDetailFieldRenderer } from './ReportDetailFieldRenderer/ReportDetailFieldRenderer';
import { ReportDetailSection } from './ReportDetailSection';

interface ReportDetailSurveillanceProps {
  report: ReportResponseDto;
  isDraftStatus: boolean;
  defaultOpen?: boolean;
}

export const ReportDetailSurveillance = ({
  isDraftStatus,
  report,
  defaultOpen = false,
}: ReportDetailSurveillanceProps): React.ReactElement => {
  const { t, language } = useAppTranslation();

  const sections: FormSection[] = useMemo(
    () => getStep2DetailFormConfig(t, language).sections ?? [],
    [language, t],
  );
  const surveillanceMissing = hasMissingRequiredFields(sections, (fieldId) =>
    getReportFieldValue(report, fieldId, language),
  );
  const labelOverrides: DetailFieldLabelOverrides = {
    description: t('reportCreation.step2.fields.description'),
  };

  return (
    <ReportDetailSection>
      <FoldableSection
        title={t('reportDetail.sections.surveillanceAndEtiology')}
        defaultOpen={defaultOpen}
        showSectionIndicator={isDraftStatus && surveillanceMissing}
        sectionIndicatorLabel={t('reportDetail.missingIndicator')}
      >
        <ReportDetailFieldRenderer
          sections={sections}
          labelOverrides={labelOverrides}
          source={{ kind: 'report', report }}
        />
      </FoldableSection>
    </ReportDetailSection>
  );
};
